import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './index.css';
import APIService from '../../Services/APIService';
import { Box, Grid, IconButton } from '@mui/material';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import Typography from '@mui/material/Typography';
import HomeIcon from '@mui/icons-material/Home';
import SearchBox from '../FlowBuilder/Common/SearchBox/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import Pagination from '../FlowBuilder/Common/Pagination/index.tsx';
import { dateFormate } from '../../utils/helper.jsx';
import useDebounce from '../Shared/Delay/index.tsx';

const Campaigns = () => {
  const [searchFilter, setSearchFilter] = useState('');
  const [list, setList] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [filters, setFilters] = useState({ search: '', page: 0, pageSize: 20, totalCount: 0 });
  const debouncedSearch = useDebounce(filters.search, 500);

  const getCampaignsList = async (filter) => {
    setIsDataFetched(true);
    let res = await APIService.get(
      `/v0/ui/campaign/assigned-campaigns/?search=${filter?.search}&next_page=${filter?.page + 1}&page_size=${filter?.pageSize}`
    );
    if (res.status === 200) {
      setList(res?.data?.list || []);
      setFilters({
        ...filters,
        search: filter?.search,
        page: filter?.page,
        totalCount: res?.data?.count,
      });
      setIsDataFetched(false);
    }
  };

  useEffect(() => {
    getCampaignsList({ ...filters, search: debouncedSearch });
  }, [debouncedSearch, filters.page, filters.pageSize]);

  const handleSearch = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 0,
    }));
  };

  const handlePageChange = (selectedPage) => {
    setFilters({ ...filters, page: selectedPage.selected });
  };
  const onPageChange = (e) => {
    getCampaignsList({ ...filters, page: e?.selected });
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Business Service Name',
      enableSorting: false,
    },
    {
      accessorKey: 'tentative_start_date',
      header: 'Start Date',
      enableSorting: false,
      Cell: ({ row }) => dateFormate(row.original?.tentative_start_date, 'DD-MM-YYYY'),
    },
    {
      accessorKey: 'tentative_end_date',
      header: 'End Date',
      enableSorting: false,
      Cell: ({ row }) => dateFormate(row.original?.tentative_end_date, 'DD-MM-YYYY'),
    },
    {
      accessorKey: 'tentative_start_date1',
      header: 'Start - End',
      enableSorting: false,
      Cell: (cell) => {
        const row = cell.row.original;
        const start = dateFormate(row?.tentative_start_date, 'DD-MM-YYYY');
        const end = dateFormate(row?.tentative_end_date, 'DD-MM-YYYY');
        return `${start} - ${end}`;
      },
    },
    {
      accessorKey: 'proposal_id',
      header: 'Checklist',
      enableSorting: false,
      Cell: (cell) => {
        const { proposal_id } = cell.row.original;
        return (
          <Link
            to={`/r/checklist/list/${proposal_id}`}
            style={{
              color: 'blue',
              cursor: 'pointer',
              textDecoration: 'none', // optional, remove underline
            }}
          >
            View Checklists
          </Link>
        );
      },
    },
    {
      accessorKey: 'action',
      header: 'Action',
      enableSorting: false,
      Cell: (cell) => {
        const { proposal_id } = cell.row.original;
        return (
          <Link
            to={`/r/checklist/suppliers/${proposal_id}`}
            style={{
              color: 'blue',
              cursor: 'pointer',
              textDecoration: 'none', // optional, remove underline
            }}
          >
            Select Supplier
          </Link>
        );
      },
    },
  ];

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Business Service Checklist' },
  ];

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setFilters((prev) => ({
      ...prev,
      pageSize: newSize,
      page: 0, // Reset to the first page
    }));
    getCampaignsList({ ...filters, pageSize: newSize, page: 0 });
  };

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '5px' }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => item?.onClick?.()}
      />

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" fontWeight="bold">
            Business Service Checklist
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ alignItems: 'center', pt: 2, pb: 2.5 }} className="search-box-style">
        <Grid item xs={12} sm={6} md={2}>
          <SearchBox handleSearch={handleSearch} value={filters?.search} />
        </Grid>
      </Grid>
      <CommonTable
        columns={columns}
        data={list}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 320px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
          },
        }}
        state={{ isLoading: isDataFetched }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              totalCount={filters?.totalCount}
              rowsPerPage={filters?.pageSize}
              page={filters?.page}
              handleChangePage={(e, newPage) => {
                setFilters((prev) => ({ ...prev, page: newPage }));
                getCampaignsList({ ...filters, page: newPage });
              }}
              handleChangeRowsPerPage={handlePageSizeChange}
            />
          </Box>
        )}
      />
    </Box>
  );
};

export default Campaigns;
